import { Component, Input, OnInit, ViewChild, inject } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import { NgSelectModule } from "@ng-select/ng-select";
import { Configuration } from "@app/app.constants";
import { PeriodicNotificationMoiModel } from "@app/mailmessages/models/periodicNotificationMoiModel";
import { PeriodInfo } from "@app/mailmessages/models/periodicNotificationMoiStandingDataModel";
import { PeriodicNotificationMoiService } from "@app/mailmessages/service/periodic-notification-moi.service";
import { CerrixEditorComponent } from "@app/shared/components/cerrix-editor/cerrix-editor.component";
import { CheckboxComponent } from "@app/shared/cerrix-checkbox/cerrix-checkbox.component";
import { CerrixSelectTreeComponent } from "@app/shared/cerrix-select-tree/cerrix-select-tree.component";
import { CerrixTextfieldComponent } from "@app/shared/components/cerrix-textinput/cerrix-textfield/cerrix-textfield.component";
import { FormValidationHelper } from "@app/shared/helpers/form-validation-helper";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { Pages } from "@constants/pages/Pages";
import { ModuleType } from "@enums/ModuleType";
import { getFormControl } from "@methods/CommonMethods";
import { isEmpty } from "@methods/isEmpty";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { TabService } from "@services/tabs/TabService";
import { ToastrService } from "ngx-toastr";
import { Observable, firstValueFrom } from "rxjs";
import { finalize } from "rxjs/operators";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/mailmessages/i18n/scope";

@Component({
    selector: "uc-periodic-notification-detail-moi",
    templateUrl: "./periodic-notification-detail-moi.component.html",
    styleUrls: ["./periodic-notification-detail-moi.component.scss"],
    imports: [
        ReactiveFormsModule,
        MatTooltipModule,
        NgSelectModule,
        CheckboxComponent,
        CerrixSelectTreeComponent,
        CerrixTextfieldComponent,
        CerrixEditorComponent,
        TranslatePipe,
    ],
    viewProviders: [provideTranslationScope(scope)]
})
export class PeriodicNotificationDetailMoiComponent implements OnInit {
    private _pnService = inject(PeriodicNotificationMoiService);
    private toastr = inject(ToastrService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);

    @ViewChild("editor") editorComponent: CerrixEditorComponent;
    @Input() module: ModuleType;
    @Input() id: number;
    @Input() cerrixTab: TabModel;

    pageReady = false;

    notification: any;
    keywords: string[];
    orgs: CerrixTreeItem[];
    mailEnabled: boolean;

    dayNumbers: IdNameCombination[] = [];
    periods: PeriodInfo[] = [];
    moiTypes: IdNameCombination[] = [];
    userTypes: IdNameCombination[] = [];
    priorities: IdNameCombination[] = [];

    form: FormGroup;
    isEmpty = false;
    organizationRequest: Observable<CerrixTreeItem[]>;
    noOrganizationsSelectedDescription = Configuration.NoOrganizationsSelectedDescription;

    subjectHasFocus: boolean;

    async ngOnInit() {
        // save(), deleteClick() and validate() translate; the template's pageReady guard keeps
        // them unreachable until the scope is ready.
        await this._translation.ready();
        var notification = await firstValueFrom(this._pnService.getNotification(Number(this.id)));
        if (this.id === 0) {
            notification.period = null;
            notification.userTypeIds = [];
            notification.dayNumber = null;
        }

        this.notification = notification;
        this.mailEnabled = this.notification.active;

        var data = await firstValueFrom(this._pnService.getNotificationStandingData());
        this.periods = data.Periods;
        this.moiTypes = data.MoiTypeOptions;
        this.userTypes = data.UserTypeOptions;
        this.priorities = data.PriorityOptions;
        this.keywords = data.Keywords;

        if (this.notification.period) {
            this.setDayNumbers(this.periods.find((x) => x.ID === this.notification.period));
        }

        this.organizationRequest = this._pnService.getOrganizations();

        this.initFormGroup(this.notification);
        this.pageReady = true;
    }

    setDayNumbers(period: PeriodInfo): void {
        this.dayNumbers = [];

        if (!period) {
            return;
        }

        for (let i = 1; i <= period.MaxDayNumber; i++) {
            this.dayNumbers.push({ ID: i, Name: i.toString() });
        }
    }

    initFormGroup(notification: PeriodicNotificationMoiModel) {
        this.form = new FormGroup({
            name: new FormControl(notification.name, [Validators.required]),
            active: new FormControl(notification.active),
            subject: new FormControl(notification.subject, [Validators.required]),
            organizations: new FormControl(notification.organizations),
        });

        this.form.addControl("moiTypeIds", new FormControl(notification.moiTypeIds));

        this.form.addControl("period", new FormControl(notification.period, Validators.required));
        this.form.addControl(
            "dayNumber",
            new FormControl(notification.dayNumber, Validators.required)
        );

        this.form.addControl("userTypeIds", new FormControl(notification.userTypeIds));
        this.form.addControl("priorityIds", new FormControl(notification.priorityIds));

        this.form.valueChanges.subscribe(() =>
            FormValidationHelper.mapToModel(this.form, this.notification)
        );
    }

    onChange(message: string) {
        this.notification.message = message;
    }

    addKeyword(keyword: string) {
        if (this.subjectHasFocus) {
            const ctrl = getFormControl(this.form, "subject");
            const value = ctrl.value;
            ctrl.setValue(value + " [" + keyword + "]");
        } else {
            this.editorComponent.insertText(" [" + keyword + "]");
        }
    }

    async save() {
        this.isEmpty = false;
        if (!this.notification.organizations) {
            this.notification.organizations = [];
        }

        if (!this.validate()) {
            return;
        }

        this.notification.id = this.id;

        const savingPrompt = this._promptService.loader(
            this._translation.t("periodicDetail.savingChanges")
        );
        var savePromise = firstValueFrom(
            this._pnService
                .saveNotification(this.notification)
                .pipe(finalize(() => savingPrompt.close()))
        );

        await savePromise
            .then((storedId: number) => {
                this.id = storedId;
                this._tabService.refresh(this._pages.MailMessages);

                this.toastr.success("", this._translation.t("periodicDetail.updateCompleted"));
            })
            .catch((_) => {
                this.toastr.error("", this._translation.t("periodicDetail.updateFailed"));
            });
    }

    deleteClick() {
        this._pnService.deleteNotification(this.id).subscribe((_) => {
            this._tabService.refresh(this._pages.MailMessages);
            this.toastr.success(this._translation.t("periodicDetail.notificationDeleted"));
            this.cerrixTab.close(false);
        });
    }

    validate(): boolean {
        const errors: string[] = [];
        if (isEmpty(this.notification.name)) {
            errors.push(this._translation.t("periodicDetail.nameRequiredError"));
        }

        if (!this.dayNumbers.find((x) => x.ID === this.notification.dayNumber)) {
            errors.push(this._translation.t("periodicDetail.invalidDaySelectedError"));
        }

        if (isEmpty(this.notification.subject)) {
            errors.push(this._translation.t("periodicDetail.subjectRequiredError"));
        }

        const messageEmpty = this.editorComponent.isEmpty();

        if (messageEmpty) {
            this.isEmpty = true;
            errors.push(this._translation.t("periodicDetail.messageRequiredError"));
        }

        if (!this.form.valid) {
            FormValidationHelper.markAllAsTouched(this.form);

            const validationErrors = FormValidationHelper.getFormControlErrors(this.form);
            let validationMessage = FormValidationHelper.getGeneralErrorMessage(validationErrors);
            errors.push(validationMessage);
        }

        if (errors.any()) {
            this.toastr.warning(errors.join("<br />"), this._translation.t("periodicDetail.validationsTitle"), {
                enableHtml: true,
            });
        }

        return errors.length === 0;
    }

    setFocusToSubject() {
        this.subjectHasFocus = true;
    }

    deFocusSubject() {
        this.subjectHasFocus = false;
    }
}
